import {
  Account,
  AccountSearchResults,
  AllSearchResults,
  Entry,
  Classification,
  Folder,
  PaginatedResponse,
  PaginatedSearchResponse
} from '@/types/cit-public-api';
import { api } from './api';
import { ENTRIES_PER_PAGE } from './constants';
export const queryFunctions = {
  getAccountDetails: async (context: {
    queryKey: [string, { accountId: string; citOrigin: string }];
  }): Promise<Account> => {
    const [, queryParams] = context.queryKey;
    const { accountId, citOrigin } = queryParams;

    const account = await api.getAccountDetails(accountId, citOrigin);
    return account;
  },
  // Get folder details
  getFolderDetails: async (context: {
    queryKey: [string, { accountId: string; folderId?: string | undefined; citOrigin: string }];
  }): Promise<Folder> => {
    const [, queryParams] = context.queryKey;
    const { accountId, folderId, citOrigin } = queryParams;
    return api.getFolderDetails(accountId, citOrigin, folderId);
  },
  getEntryDetails: async (context: {
    queryKey: [
      string,
      { accountId: string; entryId: string; folderId?: string; citOrigin: string }
    ];
  }): Promise<Entry> => {
    const [, queryParams] = context.queryKey;
    const { accountId, entryId, folderId, citOrigin } = queryParams;
    return api.getEntryDetails(accountId, entryId, citOrigin, folderId);
  },
  getAllAccounts: async (context: {
    queryKey: [string, { citOrigin: string }];
  }): Promise<Account[]> => {
    const [, queryParams] = context.queryKey;
    const { citOrigin } = queryParams;
    const response = await api.getAllAccounts(citOrigin);
    return response.results;
  },
  getAccountFolders: async (context: {
    queryKey: [string, { accountId: string; citOrigin: string }];
  }): Promise<Folder[]> => {
    const [, queryParams] = context.queryKey;
    const { accountId, citOrigin } = queryParams;
    return api.getAccountFolders(accountId, citOrigin);
  },
  getFeatured: async (context: {
    queryKey: [string, { citOrigin: string }];
  }): Promise<{
    featured_accounts: Account[];
    featured_classifications: Classification[];
  }> => {
    const [, queryParams] = context.queryKey;
    const { citOrigin } = queryParams;
    return api.getFeatured(citOrigin);
  },
  getAccountSearchResults: async (context: {
    queryKey: [string, { searchQuery: string; accountId: string; citOrigin: string }];
  }): Promise<AccountSearchResults> => {
    const [, queryParams] = context.queryKey;
    const { searchQuery, accountId, citOrigin } = queryParams;
    return api.getAccountSearchResults(searchQuery, accountId, citOrigin);
  },
  getAllSearchResults: async (context: {
    queryKey: [string, { searchQuery: string; citOrigin: string }];
  }): Promise<AllSearchResults> => {
    const [, queryParams] = context.queryKey;
    const { searchQuery, citOrigin } = queryParams;
    return api.getAllSearchResults(searchQuery, citOrigin);
  },
  getClassifications: async (context: {
    queryKey: [string, { citOrigin: string }];
  }): Promise<Classification[]> => {
    const [, queryParams] = context.queryKey;
    const { citOrigin } = queryParams;
    return api.getClassifications(citOrigin);
  },
  fetchEntriesWithPagination: async (context: {
    pageParam?: string;
    queryKey: (
      | string
      | {
          searchQuery: string | null;
          accountId: string | undefined;
          folderId: string | undefined;
          classificationId: string | undefined;
          citOrigin: string;
        }
    )[];
  }) => {
    const [, queryParams] = context.queryKey as [
      string,
      {
        searchQuery?: string;
        accountId?: string;
        folderId?: string;
        classificationId?: string;
        citOrigin: string;
      }
    ];
    const { searchQuery, accountId, folderId, classificationId, citOrigin } = queryParams;
    const cursor = context.pageParam;

    // must have either accountId or classificationId -- incorrect usage if neither provided
    if (!accountId && !classificationId) {
      throw new Error('No accountId or classificationId provided');
    }

    let response: Promise<PaginatedResponse<Entry> | PaginatedSearchResponse | undefined>;

    if (!classificationId && folderId && accountId) {
      response = api.getEntriesByFolderPaginated(
        accountId,
        folderId,
        ENTRIES_PER_PAGE,
        citOrigin,
        searchQuery,
        cursor
      );
    } else if (classificationId) {
      response = api.getEntriesByClassificationPaginated(
        classificationId,
        ENTRIES_PER_PAGE,
        citOrigin,
        searchQuery,
        cursor
      );
    } else if (accountId) {
      response = api.getEntriesByAccountPaginated(
        accountId,
        ENTRIES_PER_PAGE,
        citOrigin,
        searchQuery,
        cursor
      );
    } else {
      throw new Error('Invalid query parameters');
    }

    const responseData = await response;

    if (!responseData) {
      throw new Error('No response data');
    }

    return {
      entries: 'entries' in responseData ? responseData.entries : responseData.results || [],
      total: responseData.total,
      cursor: responseData.cursor
    };
  }
};
